import React, { useState, useEffect, useRef } from "react";
import { message } from "antd";
import { useTheme } from "../../../ThemeContext";
import { getProviders, createProvider, updateProvider, getFacilities, completeOnboarding } from "../onboarding-services";
import { getDepartmentsByFacilityId } from "../../../utils/apiClient";
import { Button, Dropdown, useMenu } from "@voicecare.ai/ui-library";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import ThemedChip from "../../common/ThemedChip";
import { medicalSpecialties } from "../../../utils/constants/medicalSpecialties";
import { cleanTaxId, isValidTaxId, cleanNPI, isValidNPI } from "../../../utils/helpers/format-phone-number";
import "./StepStyles.css";
import { useNavigate } from "react-router-dom";

const ProviderDetailsStep = ({ onFinish, onPrevious, isLoading, setIsLoading }) => {
  const { themeMode } = useTheme();

  const { selectMenu } = useMenu();
  
  const [formData, setFormData] = useState({
    fullName: "",
    npi: "",
    taxId: "",
    specialties: [],
    facilityId: "", // Single facility for onboarding
    affiliations: [{ departmentId: "", role: [] }] // Multiple departments for the facility
  });
  
  const [errors, setErrors] = useState({});
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [providerId, setProviderId] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const navigate = useNavigate();

  // Using comprehensive medical specialties from constants

  // Load existing provider and facility data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      console.log("👨‍⚕️ Loading provider and facility data...");
      
      // Load facilities first
      const facilitiesData = await getFacilities();
      console.log("🏥 Facilities loaded:", facilitiesData);
      setFacilities(facilitiesData || []);
      
      // Load providers
      const providers = await getProviders();
      console.log("👨‍⚕️ Providers response:", providers);
      
      if (providers && providers.length > 0) {
        // Load first provider for editing
        const provider = providers[0];
        console.log("👨‍⚕️ Loading first provider:", provider);
        setProviderId(provider._id);
        
        // Map API response to form data
        const mappedData = {
          fullName: provider.fullName || "",
          npi: provider.npi || "",
          taxId: provider.taxId || "",
          specialties: provider.specialties ? provider.specialties.map(specialty => {
            // Convert string to object format expected by dropdown
            const existingOption = medicalSpecialties.find(option => option.value === specialty);
            return existingOption || { label: specialty, value: specialty };
          }) : [],
          // Extract facility from first affiliation (onboarding uses single facility)
          facilityId: provider.affiliations?.[0]?.facilityId || "",
          // Map departments from all affiliations (assuming same facility)
          affiliations: provider.affiliations && provider.affiliations.length > 0 
            ? provider.affiliations.map(aff => ({
                departmentId: aff.departmentId || "",
                role: aff.role || []
              }))
            : [{ departmentId: "", role: [] }]
        };
        
        setFormData(mappedData);
        console.log("✅ Provider data populated:", mappedData);
        
        // If provider has facility, load departments
        if (mappedData.facilityId) {
          fetchDepartments(mappedData.facilityId);
        }
      } else {
        console.log("ℹ️ No providers found, ready to create new one");
      }
      
      setHasLoadedData(true);
    } catch (error) {
      console.error("❌ Error loading provider data:", error);
      setHasLoadedData(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSpecialtyChange = (selectedSpecialties) => {
    // Ensure selectedSpecialties is always an array and handle both string and object formats
    let specialtiesArray = [];
    if (Array.isArray(selectedSpecialties)) {
      specialtiesArray = selectedSpecialties;
    } else if (selectedSpecialties) {
      // If it's a single item, wrap it in an array
      specialtiesArray = [selectedSpecialties];
    }
    
    console.log("🔄 Specialty change:", { original: selectedSpecialties, processed: specialtiesArray });
    setFormData(prev => ({ ...prev, specialties: specialtiesArray }));
    // Clear error when user changes specialties
    if (errors.specialties) {
      setErrors(prev => ({ ...prev, specialties: null }));
    }
  };

  // Fetch departments for selected facility
  const fetchDepartments = async (facilityId) => {
    if (!facilityId) {
      setDepartments([]);
      return;
    }

    setLoadingDepartments(true);
    try {
      console.log('🏬 Fetching departments for facility:', facilityId);
      const response = await getDepartmentsByFacilityId(facilityId);
      
      // Handle different response formats (same logic as AffiliationManager)
      let departmentsData = [];
      if (response) {
        if (Array.isArray(response)) {
          departmentsData = response;
        } else if (response.response && Array.isArray(response.response)) {
          departmentsData = response.response;
        } else if (response.data && Array.isArray(response.data)) {
          departmentsData = response.data;
        }
      }

      console.log('🏬 Departments found:', departmentsData.length, departmentsData);
      setDepartments(departmentsData || []);
      
    } catch (error) {
      console.error('❌ Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  // Handle facility selection (single facility for onboarding)
  const handleFacilityChange = (facilityId) => {
    setFormData(prev => ({
      ...prev,
      facilityId: facilityId,
      affiliations: [{ departmentId: "", role: [] }] // Reset departments when facility changes
    }));
    
    // Fetch departments for the new facility
    fetchDepartments(facilityId);
    
    // Clear any existing errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.facility;
      // Clear all department errors
      Object.keys(newErrors).forEach(key => {
        if (key.includes('department') || key.includes('role')) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  const handleAffiliationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      affiliations: prev.affiliations.map((aff, i) => 
        i === index ? { ...aff, [field]: value } : aff
      )
    }));
  };



  const handleRoleRemove = (affiliationIndex, roleToRemove) => {
    const updatedAffiliations = [...formData.affiliations];
    updatedAffiliations[affiliationIndex].role = updatedAffiliations[affiliationIndex].role.filter(r => r !== roleToRemove);
    setFormData(prev => ({ ...prev, affiliations: updatedAffiliations }));
  };

  // Custom component for roles chip input with Add button and onBlur support
  const AffiliationRolesInput = ({ affiliationIndex, roles, themeMode, error }) => {
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef();

    const handleAddRole = (e) => {
      if (e) e.preventDefault();
      const value = inputValue.trim();
      if (value && !roles.includes(value)) {
        const updatedAffiliations = [...formData.affiliations];
        updatedAffiliations[affiliationIndex].role.push(value);
        setFormData(prev => ({ ...prev, affiliations: updatedAffiliations }));
        setInputValue('');
        // Clear error
        if (errors[`affiliation_${affiliationIndex}_role`]) {
          setErrors(prev => ({ ...prev, [`affiliation_${affiliationIndex}_role`]: null }));
        }
      }
    };

    const handleDeleteRole = (roleToDelete) => {
      handleRoleRemove(affiliationIndex, roleToDelete);
    };

    const handleBlur = () => {
      // Add role on blur (when user leaves the input field)
      if (inputValue.trim()) {
        handleAddRole();
      }
    };

    return (
      <div style={{ width: '100%' }}>
        {/* Input and Add Button Row */}
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          alignItems: 'stretch',
          marginBottom: '0.5rem'
        }}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ',') {
                handleAddRole(e);
              }
            }}
            onBlur={handleBlur}
            className={`form-input ${error ? 'error' : ''}`}
            style={{ 
              flex: 1,
              borderRadius: '6px',
              border: error ? '2px solid #ff4d4f' : '1px solid #d9d9d9',
              backgroundColor: themeMode === 'dark' ? '#1a1a1a' : '#ffffff',
              color: themeMode === 'dark' ? '#ffffff' : '#000000',
              padding: '8px 12px'
            }}
            placeholder="Add role..."
            disabled={isLoading}
          />
          <Button
            variant="contained"
            onClick={handleAddRole}
            label="Add"
            disabled={isLoading || !inputValue.trim()}
            style={{ 
              borderRadius: "6px",
              fontSize: '12px',
              padding: '8px 16px',
              minWidth: 'auto'
            }}
          />
        </div>

        {/* Roles Display */}
        {roles.length > 0 && (
          <div style={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.25rem'
          }}>
            {roles.map((role, chipIdx) => (
              <ThemedChip
                key={chipIdx}
                variant="default"
                size="small"
                customStyle={{
                  padding: "0.2rem 0.5rem",
                  borderRadius: "0.25rem",
                  cursor: "pointer",
                  position: "relative",
                  paddingRight: "1.5rem"
                }}
              >
                {role}
                <span
                  onClick={() => handleDeleteRole(role)}
                  style={{
                    position: "absolute",
                    right: "0.25rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "bold",
                    color: themeMode === 'dark' ? '#ffffff' : '#666666',
                    hover: { color: themeMode === 'dark' ? '#ff6b6b' : '#d32f2f' }
                  }}
                >
                  ×
                </span>
              </ThemedChip>
            ))}
          </div>
        )}
      </div>
    );
  };

  const addDepartment = () => {
    setFormData(prev => ({
      ...prev,
      affiliations: [...prev.affiliations, { departmentId: "", role: [] }]
    }));
  };

  const removeDepartment = (index) => {
    if (formData.affiliations.length > 1) {
      setFormData(prev => ({
        ...prev,
        affiliations: prev.affiliations.filter((_, i) => i !== index)
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full Name is required";
    }
    
    if (!formData.npi.trim()) {
      newErrors.npi = "NPI is required";
    } else if (!isValidNPI(formData.npi)) {
      newErrors.npi = "NPI must be exactly 10 digits";
    }
    
    if (!formData.taxId.trim()) {
      newErrors.taxId = "Tax ID is required";
    } else if (!isValidTaxId(formData.taxId)) {
      newErrors.taxId = "Tax ID must be exactly 9 digits";
    }
    
    if (formData.specialties.length === 0) {
      newErrors.specialties = "At least one specialty is required";
    }
    
    // Validate facility selection
    if (!formData.facilityId) {
      newErrors.facility = "Facility selection is required";
    }
    
    // Validate departments and roles
    if (formData.affiliations.length === 0) {
      newErrors.affiliations = "At least one department is required";
    } else {
      formData.affiliations.forEach((aff, index) => {
        // Check if department is required when facility has departments
        if (formData.facilityId && departments.length > 0 && !aff.departmentId) {
          newErrors[`affiliation_${index}_department`] = "Department is required for this facility";
        }
        
        if (aff.role.length === 0) {
          newErrors[`affiliation_${index}_role`] = "At least one role is required";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFinish = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      
      const payload = {
        fullName: formData.fullName,
        npi: cleanNPI(formData.npi),
        taxId: cleanTaxId(formData.taxId),
        specialties: formData.specialties.map(specialty => 
          typeof specialty === 'string' ? specialty : specialty.value
        ),
        // Convert back to API format with facilityId in each affiliation
        affiliations: formData.affiliations.map(aff => {
          const affiliation = {
            facilityId: formData.facilityId,
            role: aff.role
          };
          
          // Only include departmentId if it has a value
          if (aff.departmentId && aff.departmentId.trim()) {
            affiliation.departmentId = aff.departmentId;
          }
          
          return affiliation;
        })
      };

      let providerResponse;
      if (providerId) {
        // Update existing provider
        providerResponse = await updateProvider(providerId, payload);
        if (providerResponse.statusCode === "SUCCESS") {
          message.success("Provider details updated successfully!");
        } else {
          message.error(providerResponse.message || "Failed to update provider details");
          return;
        }
      } else {
        // Create new provider
        providerResponse = await createProvider(payload);
        if (providerResponse.statusCode === "SUCCESS") {
          message.success("Provider details saved successfully!");
        } else {
          message.error(providerResponse.message || "Failed to save provider details");
          return;
        }
      }

      // Mark onboarding as complete
      console.log("🎯 Completing onboarding process...");
      const completionResponse = await completeOnboarding();
      if (completionResponse.statusCode === "SUCCESS") {
        message.success("Onboarding completed successfully!");
        selectMenu("Billing");
        // Redirect to billing page
        setTimeout(() => {
          navigate("/billing");
          // Refresh the page after navigation to ensure clean state
          setTimeout(() => {
            window.location.reload();
          }, 100);
        }, 1500);
      } else {
        console.warn("Onboarding completion response:", completionResponse);
        // Still proceed even if completion fails
        onFinish();
      }
    } catch (error) {
      console.error("Error saving provider details:", error);
      message.error("Failed to save provider details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasLoadedData) {
    return (
      <div className="step-loading">
        <div className="loading-spinner"></div>
        <p>Loading provider details...</p>
      </div>
    );
  }

  return (
    <div className={`step-container ${themeMode}`} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="step-form" style={{ flex: "1", overflowY: "auto", paddingBottom: "0.5rem" }}>
        {/* Provider Details Section */}
        <div className="form-section">
          <h3 className="form-section-title">Provider Details</h3>
          
          <div className="form-row">
            <div className="form-field">
              <label className="form-label">Full Name*</label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => handleInputChange("fullName", e.target.value)}
                className={`form-input ${errors.fullName ? "error" : ""}`}
                disabled={isLoading}
                placeholder="Dr. John Smith"
              />
              <div className="form-error-container">
                {errors.fullName && <span className="form-error">{errors.fullName}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">NPI*</label>
              <input
                type="text"
                value={formData.npi}
                onChange={(e) => handleInputChange("npi", e.target.value)}
                className={`form-input ${errors.npi ? "error" : ""}`}
                disabled={isLoading}
                placeholder="1234567890"
              />
              <div className="form-error-container">
                {errors.npi && <span className="form-error">{errors.npi}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">Tax ID*</label>
              <input
                type="text"
                value={formData.taxId}
                onChange={(e) => handleInputChange("taxId", e.target.value)}
                className={`form-input ${errors.taxId ? "error" : ""}`}
                disabled={isLoading}
                placeholder="123456789"
              />
              <div className="form-error-container">
                {errors.taxId && <span className="form-error">{errors.taxId}</span>}
              </div>
            </div>
          </div>

          {/* Specialties Section */}
          <div className="form-row">
            <div className="form-field" style={{ gridColumn: "1 / -1" }}>
              <label className="form-label">Specialties*</label>
              <div style={{ 
                border: errors.specialties ? "2px solid #ff4d4f" : "1px solid #d9d9d9",
                borderRadius: "6px",
                padding: "2px"
              }}>
                <Dropdown
                  value={formData.specialties}
                  onChange={handleSpecialtyChange}
                  options={medicalSpecialties}
                  type="multi-select"
                  isSearchable={true}
                  creatable={true}
                  placeholder="Select or add specialties"
                  valueKey="value"
                  labelKey="label"
                  themeMode={themeMode}
                  disabled={isLoading}
                  style={{ 
                    width: "100%", 
                    borderRadius: "6px", 
                    marginBottom: "0.5rem",
                    border: "none"
                  }}
                />
              </div>
              <div className="form-error-container">
                {errors.specialties && <span className="form-error">{errors.specialties}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Facility & Departments Section */}
        <div className="form-section">
          <h3 className="form-section-title">Affiliations</h3>
          
          {/* Facility Selection - Single for onboarding */}
          <div className="form-row" style={{ marginBottom: "1rem" }}>
            <div className="form-field" style={{ gridColumn: "1 / -1" }}>
              <label className="form-label">Facility*</label>
              <select
                value={formData.facilityId}
                onChange={(e) => handleFacilityChange(e.target.value)}
                className={`form-select ${errors.facility ? "error" : ""}`}
                disabled={isLoading}
                style={{ 
                  width: "100%", 
                  borderRadius: "6px",
                  border: errors.facility ? "2px solid #ff4d4f" : "1px solid #d9d9d9",
                  backgroundColor: themeMode === "dark" ? "#1a1a1a" : "#ffffff",
                  color: themeMode === "dark" ? "#ffffff" : "#000000"
                }}
              >
                <option value="">Select Facility</option>
                {facilities.map((facility) => (
                  <option key={facility._id} value={facility._id}>
                    {facility.facilityName} - {facility.location?.streetAddress || facility.location?.city}
                  </option>
                ))}
              </select>
              <div className="form-error-container">
                {errors.facility && (
                  <span className="form-error">{errors.facility}</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Departments & Roles - Only show if facility is selected */}
          {formData.facilityId && (
            <div style={{ 
              maxHeight: "22vh", 
              overflowY: "auto", 
              paddingRight: "0.5rem",
              marginBottom: "0.5rem",
              border: `1px solid ${themeMode === "dark" ? "#404040" : "#e0e0e0"}`,
              borderRadius: "6px",
              padding: "0.5rem"
            }}>
              {formData.affiliations.map((affiliation, index) => (
                <div 
                  key={index} 
                  style={{ 
                    marginBottom: "0.5rem", 
                    padding: "0.75rem", 
                    border: `1px solid ${themeMode === "dark" ? "#404040" : "#e0e0e0"}`, 
                    borderRadius: "6px",
                    backgroundColor: themeMode === "dark" ? "#1a1a1a" : "#ffffff"
                  }}
                >
                  <div className="form-row">
                    {/* Department Selection - Show only if facility has departments */}
                    {departments.length > 0 ? (
                      <div className="form-field">
                        <label className="form-label">Department*</label>
                        <select
                          value={affiliation.departmentId}
                          onChange={(e) => handleAffiliationChange(index, "departmentId", e.target.value)}
                          className={`form-select ${errors[`affiliation_${index}_department`] ? "error" : ""}`}
                          disabled={isLoading || loadingDepartments}
                          style={{ 
                            width: "100%", 
                            borderRadius: "6px",
                            border: errors[`affiliation_${index}_department`] ? "2px solid #ff4d4f" : "1px solid #d9d9d9",
                            backgroundColor: themeMode === "dark" ? "#1a1a1a" : "#ffffff",
                            color: themeMode === "dark" ? "#ffffff" : "#000000"
                          }}
                        >
                          <option value="">
                            {loadingDepartments ? "Loading departments..." : "Select Department"}
                          </option>
                          {departments
                            .filter((department) => {
                              // Filter out departments already selected in other affiliations
                              const selectedDepartmentIds = formData.affiliations
                                .map((aff, idx) => idx !== index ? aff.departmentId : null) // Exclude current dropdown
                                .filter(Boolean);
                              return !selectedDepartmentIds.includes(department._id);
                            })
                            .map((department) => (
                            <option key={department._id} value={department._id}>
                              {department.name}
                            </option>
                          ))}
                        </select>
                        <div className="form-error-container">
                          {errors[`affiliation_${index}_department`] && (
                            <span className="form-error">{errors[`affiliation_${index}_department`]}</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="form-field">
                        <label className="form-label">Facility-Level Role</label>
                        <p style={{ 
                          fontSize: "0.875rem", 
                          color: themeMode === "dark" ? "#888" : "#666",
                          fontStyle: "italic",
                          margin: "0.5rem 0"
                        }}>
                          This facility has no departments. Adding facility-level roles.
                        </p>
                      </div>
                    )}
                    
                    <div className="form-field">
                      <label className="form-label">Roles*</label>
                      <AffiliationRolesInput
                        affiliationIndex={index}
                        roles={affiliation.role}
                        themeMode={themeMode}
                        error={errors[`affiliation_${index}_role`]}
                      />
                      <div className="form-error-container">
                        {errors[`affiliation_${index}_role`] && (
                          <span className="form-error">{errors[`affiliation_${index}_role`]}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-field" style={{ display: "flex", alignItems: "flex-end", gap: "0.5rem" }}>
                      {index > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => removeDepartment(index)}
                          sx={{ color: '#FF4D4F', mr: 1 }}
                          disabled={isLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                      {index === formData.affiliations.length - 1 && departments.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Button
                            size="small"
                            label={`Add Department (${formData.affiliations.length}/${departments.length})`}
                            onClick={addDepartment}
                            style={{ borderRadius: '6px' }}
                            disabled={isLoading || formData.affiliations.length >= departments.length}
                          />
                          {formData.affiliations.length >= departments.length && (
                            <span style={{
                              fontSize: '12px',
                              color: themeMode === 'dark' ? '#888' : '#666',
                              fontStyle: 'italic',
                              marginTop: '4px'
                            }}>
                              All available departments added
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Action Buttons - Fixed at bottom */}
      <div className="step-actions has-previous" style={{ 
        flexShrink: 0, 
        marginTop: "auto",
        paddingTop: "0.5rem",
        borderTop: `1px solid ${themeMode === "dark" ? "#404040" : "#e0e0e0"}`,
        backgroundColor: themeMode === "dark" ? "#1a1a1a" : "#ffffff"
      }}>
        <button
          className="btn-previous"
          onClick={onPrevious}
          disabled={isLoading}
        >
          Previous
        </button>
        <button
          className="btn-next"
          onClick={handleFinish}
          disabled={isLoading}
        >
          {isLoading ? "Completing..." : "Complete Onboarding"}
        </button>
      </div>
    </div>
  );
};

export default ProviderDetailsStep; 